// RAG-lite: knowledge-base slices injected into AI-helper CONTEXT.
//
// Why a hook (not just plain functions): the underlying datasets are loaded
// via react-query (property_tax.json, passport_treatment.json, etc.). The hook
// surfaces loading-states-aware results so callers don't have to wire multiple
// queries by hand at every surface. Returns an empty slice list while
// any query is pending; the AI bubble simply has less context but still works.
//
// Design contract: every slice is small (<800 bytes JSON). Capped at 5
// countries to stay under the AI helper's 8 KB sanitizeContext budget.
//
// Stage 5.0 (2026-05-25): added visa_extra + tax_regimes slices.

import { useMemo } from 'react';
import {
  buildAiContextBlock,
  usePropertyTax,
  type PropertyTaxAiBlock,
} from '@/features/property-tax';
import {
  resolveTreatment,
  usePassportTreatment,
  type TreatmentStatus,
} from '@/features/quiz/passport-treatment';
import {
  buildVisaAiBlock,
  useCountryVisaExtra,
  type VisaAiBlock,
} from '@/features/quiz/visa-extra';
import {
  buildTaxAiBlock,
  useCountryTaxRegimes,
  type TaxAiBlock,
} from '@/features/quiz/tax-regimes';
import {
  buildCarAiBlock,
  useCarOwnership,
  type CarOwnershipAiBlock,
} from '@/features/car-ownership';
import type { Passport, VisaPathway } from '@/features/quiz/types';

export interface PassportTreatmentSlice {
  status: TreatmentStatus;
  evidence: string;
}

export interface CountryRagSlice {
  country_code: string;
  property_tax: PropertyTaxAiBlock | null;
  passport_treatment: PassportTreatmentSlice | null;
  /** Phase 3: visa pathway detail for the pathways the user selected. */
  visa_detail: VisaAiBlock | null;
  /** Phase 3: tax regime summary tuned to the user's tax intent. */
  tax_regime: TaxAiBlock | null;
  /** Stage 5.1: car ownership costs + DL info. */
  car_ownership: CarOwnershipAiBlock | null;
}

const MAX_SLICES = 5;

/**
 * Build per-country slices for the supplied countryCodes. Slices include:
 * - property_tax: always (property tax rates + notes)
 * - passport_treatment: only ru/by/ua passports (others uniformly welcoming)
 * - visa_detail: when user selected specific visa pathways
 * - tax_regime: always (tax regime + intent-tuned notes)
 */
export function useCountryRagSlices(
  countryCodes: string[],
  passport: Passport | undefined,
  lang: 'en' | 'ru',
  selectedVisaPathways?: VisaPathway[],
): CountryRagSlice[] {
  const { data: propTaxFile } = usePropertyTax();
  const { data: treatmentFile } = usePassportTreatment();
  const { data: visaExtraFile } = useCountryVisaExtra();
  const { data: taxRegimesFile } = useCountryTaxRegimes();
  const { data: carFile } = useCarOwnership();

  return useMemo(() => {
    const codes = countryCodes.slice(0, MAX_SLICES);
    return codes.map((code): CountryRagSlice => {
      const upper = code.toUpperCase();
      const propertyTax = buildAiContextBlock(propTaxFile, upper, lang);
      const treatment = resolveTreatment(passport, upper, treatmentFile, lang);
      const visaDetail = buildVisaAiBlock(
        visaExtraFile,
        upper,
        selectedVisaPathways ?? [],
        lang,
      );
      const taxRegime = buildTaxAiBlock(taxRegimesFile, upper, lang);
      const carOwnership = buildCarAiBlock(carFile, upper, lang);
      return {
        country_code: upper,
        property_tax: propertyTax,
        passport_treatment: treatment
          ? { status: treatment.status, evidence: treatment.evidence }
          : null,
        visa_detail: visaDetail,
        tax_regime: taxRegime,
        car_ownership: carOwnership,
      };
    });
  }, [countryCodes, passport, lang, selectedVisaPathways, propTaxFile, treatmentFile, visaExtraFile, taxRegimesFile, carFile]);
}
